import { useOutletContext, useParams } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import Button from '@carrier-io/air-react/Button';
import Stack from '@carrier-io/air-react/Stack';
import { ConnectionContextType } from '../Connection';
import NextArrow from 'components/icons/NextArrow';
import ButtonBase from '@carrier-io/air-react/ButtonBase';
import Typography from '@carrier-io/air-react/Typography';
import Divider from '@carrier-io/air-react/Divider';
import CopyToClipboard from './CopyToClipboard';
import CertificateStatus from './CertificateStatus';
import { config } from '../../../config';
import { Align, TypographyColor, Variant } from 'constants/componentEnums';
import { isIdValid } from 'utils/validators';
import { useNotification } from 'context/NotificationContext';
import {
  isCertificateAdded,
  isProjectOngoing,
  isUploadingDisabled,
  ProjectOnboardingStatus,
  UpdatedProjectSchema,
} from 'store/models/Project';
import { NotificationBgColor } from 'store/models/Notification';
import { UPDATE_PROJECT } from 'store/graphql/mutations/UPDATE_PROJECT';
import styles from '../../../styles/Connection.module.css';

const TestConnection = () => {
  const { handleChange, cancelConfigure, refetchVendorData, vendorData } = useOutletContext<ConnectionContextType>();
  const { setIsNotificationOpened, setTextContent, setNotificationBgColor } = useNotification();
  const { projectId = '' } = useParams();
  const formattedProjectId = projectId?.toLocaleLowerCase();
  const currentProject = vendorData?.projects?.find((project) => project.id === formattedProjectId);
  const projectStatus: ProjectOnboardingStatus = currentProject?.status ?? ProjectOnboardingStatus.DRAFT;
  const isStatusAdded = isCertificateAdded(projectStatus);
  const isCertificateAlreadyUploaded = isProjectOngoing(projectStatus);
  const disableUpload = isUploadingDisabled(projectStatus);
  const [updateProject, { loading: updatingLoading }] = useMutation(UPDATE_PROJECT);

  const isValidUpdatedProject = ({ vendorId, projectId }: UpdatedProjectSchema) => {
    let errorMessage = '';
    let isValid = false;
    const isIdsValid = isIdValid(vendorId) && isIdValid(projectId);

    if (!isStatusAdded) {
      errorMessage = 'You should uplaod certificate before next step';
    } else if (!isIdsValid) {
      errorMessage = 'Invalid payload data';
    } else if (isIdsValid && isStatusAdded) {
      isValid = true;
    }

    return { errorMessage, isValid };
  };

  const handleUpdateProject = async () => {
    const updatedProjectData: UpdatedProjectSchema = {
      vendorId: vendorData?.vendorId ?? '',
      projectId: currentProject?.id ?? '',
      project: {
        status: ProjectOnboardingStatus.CONNECTED,
      },
    };

    const { isValid, errorMessage } = isValidUpdatedProject(updatedProjectData);

    if (isValid) {
      try {
        const response = await updateProject({
          variables: updatedProjectData,
        });
        console.log('Update Project Response: ', response.data);
        setTextContent('The project has been successfully connected');
        setNotificationBgColor(NotificationBgColor.SUCCESS);
        setIsNotificationOpened(true);
        refetchVendorData();
        handleChange(2, 3);
      } catch (err) {
        setTextContent('There was an issue with your request');
        setNotificationBgColor(NotificationBgColor.ERROR);
        setIsNotificationOpened(true);
        console.error('Update Project Status Error: ', err);
      }
    } else {
      setTextContent(errorMessage);
      setNotificationBgColor(NotificationBgColor.WARNING);
      setIsNotificationOpened(true);
    }
  };

  const handleSubmit = () => {
    if (isStatusAdded) {
      handleUpdateProject();
    } else {
      handleChange(2, 3);
    }
  };

  return (
    <Stack direction="column" spacing={2} width="80%">
      <Typography
        color={TypographyColor.Text.Primary}
        sx={{
          marginTop: '20px',
          marginBottom: '20px',
        }}
        variant={Variant.H4}
        align={Align.Left}
      >
        Test Your Connection
      </Typography>
      <Divider variant="full" />
      <Typography color={TypographyColor.Text.Primary} variant={Variant.H6} align={Align.Left}>
        Execute the Connection Script
      </Typography>
      <CopyToClipboard text={'npm run test-connection'} />
      <Typography color={TypographyColor.Text.Primary} variant={Variant.Body1} align={Align.Left}>
        You should see output similar to the following:
      </Typography>
      <Typography
        className={styles.configField}
        color={TypographyColor.Text.Primary}
        variant={Variant.Body2}
        align={Align.Left}
      >
        Starting Connection command
        <br />
        Borker URL: {config.mqttURL}
        <br />
        Path to CA certificate: ./ca/AmazonRootCA1.pem
        <br />
        Path to device certificate: ./ca/deviceCertAndCACert.crt
        <br />
        Path to device key: ./ca/deviceCert.key
        <br />
        Command's execution status: Connection command is started
        <br />
        Successfully connected to MQTT broker. ✔
      </Typography>
      <Typography color={TypographyColor.Text.Primary} variant={Variant.Body1} align={Align.Left}>
        CAUTION: If Connection Lost error is shown, please re-run command one more time.
      </Typography>
      <Typography color={TypographyColor.Text.Primary} variant={Variant.Body1} align={Align.Left}>
        If you observe errors, please ensure that your internet connection is fine and go back to the previuos step to
        verify test application configuration. If none helps, please contact Carrier support.
      </Typography>
      {(isCertificateAlreadyUploaded || disableUpload) && <CertificateStatus status={projectStatus} />}
      <Divider variant="full" />
      <Stack direction="row" spacing={2} alignSelf="flex-end">
        <ButtonBase onClick={cancelConfigure}>
          <Typography data-testid="step-cancel" color="primary">
            Cancel
          </Typography>
        </ButtonBase>
        <Button
          data-testid="proceed-test-payload"
          disabled={updatingLoading}
          endIcon={<NextArrow />}
          color="primary"
          fullWidth={false}
          onClick={handleSubmit}
        >
          Next: Test a Payload
        </Button>
      </Stack>
    </Stack>
  );
};

export default TestConnection;
